import {useCallback} from 'react';
import {TextSelection} from 'prosemirror-state';
import {blockRangeToDocPositions} from '../../../helpers/blockRangeToDocPositions';

/**
 * Hook to link an entity for an entity_recognition task.
 *
 * It selects the task's referenced text in the editor and then triggers the same
 * global `openEntitySearch` event used by the floating menu / toolbar.
 *
 * @param {Object} params
 * @param {import('prosemirror-view').EditorView | null | undefined} params.view
 * @param {Object} params.task
 * @returns {{handleLinkEntity: Function, canLinkEntity: boolean}}
 */
export function useLinkEntityFromTask({view, task}) {
    const canLinkEntity = !!(view && task?.blockId && Array.isArray(task?.range) && task.range.length === 2);

    const handleLinkEntity = useCallback(() => {
        if (!view || !task?.blockId || !Array.isArray(task?.range) || task.range.length !== 2) return;

        const [blockStart, blockEnd] = task.range;
        const docRange = blockRangeToDocPositions(view, task.blockId, blockStart, blockEnd);
        if (!docRange) return;

        const {from, to} = docRange;
        const {state} = view;

        // Set selection to the task range so the entity search dialog can use it as the query.
        const tr = state.tr.setSelection(TextSelection.create(state.doc, from, to));
        view.dispatch(tr);
        view.focus();

        const event = new CustomEvent('openEntitySearch', {
            detail: {view}
        });
        window.dispatchEvent(event);
    }, [view, task]);

    return {handleLinkEntity, canLinkEntity};
}

